import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Calendar,
  Users,
  Settings,
  Building2,
  Palette,
  LogOut,
  FileText,
  Share2,
  Building,
  User,
  Calculator,
  Receipt,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useAuth, isSuperAdmin } from '../../lib/auth';
import ImageWithFallback from '../ImageWithFallback';

const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard, enabled: true },
  { name: 'Espace agence', href: '/admin/profile', icon: Building2, enabled: true },
  { name: 'Mes Packages', href: '/admin/packages', icon: Package, enabled: true },
  { name: 'Explorer les séjours', href: '/admin/bookings/new', icon: Search, enabled: false },
  { name: 'Gestion des Réservations', href: '/admin/bookings', icon: Calendar, enabled: false },
  { name: 'Mes Réservations', href: '/admin/my-bookings', icon: Calendar, enabled: false },
  { name: 'Devis', href: '/admin/quotes', icon: FileText, enabled: false },
  { name: 'Clients', href: '/admin/customers', icon: Users, enabled: true },
  { name: 'Entreprises', href: '/admin/business-partners', icon: Building, enabled: false },
  { name: 'Comptabilité', href: '/admin/accounting', icon: Calculator, enabled: false },
  { name: 'Facturation', href: '/admin/billing', icon: Receipt, enabled: false },
  { name: 'Réseaux Sociaux', href: '/admin/social', icon: Share2, enabled: false },
  { name: 'Personnalisation', href: '/admin/theme', icon: Palette, enabled: false },
  { name: 'Paramètres', href: '/admin/settings', icon: Settings, enabled: false },
];

const superAdminNav = { name: 'Super Admin', href: '/superadmin', icon: ShieldCheck, enabled: true };

interface AdminSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export default function AdminSidebar({ isOpen = false, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [userData, setUserData] = useState<{ 
    email: string; 
    agency: {
      name: string;
      logo: string | null;
    }
  } | null>(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserData(user);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fonction pour générer les initiales
  const getInitials = (email: string) => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const handleLinkClick = () => {
    onClose?.();
  };

  return (
    <div
      className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200
        transform transition-transform duration-200 ease-out
        md:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center flex-shrink-0 px-4 h-16 border-b border-gray-200">
          {userData?.agency?.logo ? (
            <ImageWithFallback
              src={userData.agency.logo}
              alt={userData.agency.name}
              className="h-10 w-10 object-cover rounded-full"
            />
          ) : (
            <Package className="h-10 w-10 text-blue-600" />
          )}
          <div className="ml-3">
            <span className="text-sm font-medium text-gray-900">
              {userData?.agency?.name || 'Espace agence'}
            </span>
          </div>
        </div>
        
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {(isSuperAdmin() ? [superAdminNav] : navigation)
            .filter((item) => item.enabled)
            .map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.name}
                  className={`
                    group flex items-center px-2 py-2 text-sm font-medium rounded-md
                    ${location.pathname === item.href
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <Link
                    to={item.href}
                    className="flex items-center w-full"
                    onClick={handleLinkClick}
                  >
                    <Icon className="mr-3 h-6 w-6" />
                    {item.name}
                  </Link>
                </div>
              );
            })}
        </nav>

        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          <button 
            onClick={handleLogout}
            className="flex-shrink-0 w-full group block"
          >
            <div className="flex items-center">
              {userData?.email ? (
                <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                  {getInitials(userData.email)}
                </div>
              ) : (
                <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-500" />
                </div>
              )}
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  {userData?.email?.split('@')[0] || 'Utilisateur'}
                </p>
                <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                  Déconnexion
                </p>
              </div>
              <LogOut className="ml-auto h-5 w-5 text-gray-400" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}